import styled, { css } from 'styled-components'
import { Flex } from '@/components/priority/Flex/Flex.styled'
import { colors } from '@/utils/styling/colors'

const TRIM_MARK_OFFSET = 13 // Distance from content edge to start of trim mark
const TRIM_MARK_BLEED = 20 // Distance from content edge to end of bleed area
const TRIM_MARK_LENGTH = 24 // Total length of trim mark line (0.250 inches at 96 DPI)
const TRIM_MARK_THICKNESS = 1 // Thickness of trim mark line

type TrimMarkCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center'
  | 'sides'

export const Div = styled.div(
  () => css`
    body {
      margin: 0;
    }
  `,
)

export const Indicator = styled(Flex)<{ $active?: boolean }>(
  ({ $active }) => css`
    background: ${colors($active ? 'brand' : 'gray4')};
    display: flex;
    justify-content: center;
    align-items: center;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  `,
)

export const PageProofContainer = styled.div(
  () => css`
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    position: relative;
    background: #f5f5f5;
    display: flex;
    flex-direction: column;
    margin-bottom: 0;

    &:not(:last-child) {
      page-break-after: always;
    }

    &:last-child {
      margin-bottom: 0;
    }
  `,
)

export const PageProofContentArea = styled.div(
  () => css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
  `,
)

export const PageProofWrapper = styled.div<{
  $contentWidth: number
  $contentHeight: number
}>(
  ({ $contentWidth, $contentHeight }) => css`
    width: ${$contentWidth + TRIM_MARK_BLEED * 2}px;
    height: ${$contentHeight + TRIM_MARK_BLEED * 2}px;
    position: relative;
    display: inline-block;
    /* Center the content within the expanded wrapper */
    padding: ${TRIM_MARK_BLEED}px;
    box-sizing: border-box;
  `,
)

export const PageProofContent = styled.div<{
  $contentWidth: number
  $contentHeight: number
}>(
  ({ $contentWidth, $contentHeight }) => css`
    width: ${$contentWidth}px;
    height: ${$contentHeight}px;
    position: relative;
  `,
)

export const TrimMarks = styled.div<{
  $contentWidth: number
  $contentHeight: number
  $productBleedClipped: boolean
  $corner: TrimMarkCorner
  $showVerticalCenter?: boolean
  $showHorizontalCenter?: boolean
}>(
  ({
    $contentWidth,
    $contentHeight,
    $productBleedClipped,
    $corner,
    $showVerticalCenter,
    $showHorizontalCenter,
  }) => {
    if (!$productBleedClipped) return css``

    const baseStyles = css`
      position: absolute;
      top: ${TRIM_MARK_BLEED}px;
      left: ${TRIM_MARK_BLEED}px;
      width: ${$contentWidth}px;
      height: ${$contentHeight}px;
      pointer-events: none;
    `

    switch ($corner) {
      case 'top-left':
        return css`
          ${baseStyles}

          /* Top left horizontal line (pointing right toward corner) */
        &::before {
            content: '';
            position: absolute;
            top: ${TRIM_MARK_OFFSET}px;
            left: -${TRIM_MARK_BLEED}px;
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }

          /* Top left vertical line (pointing down toward corner) */
          &::after {
            content: '';
            position: absolute;
            top: -${TRIM_MARK_BLEED}px;
            left: ${TRIM_MARK_OFFSET}px;
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }
        `
      case 'top-right':
        return css`
          ${baseStyles}

          /* Top right horizontal line (pointing left toward corner) */
        &::before {
            content: '';
            position: absolute;
            top: ${TRIM_MARK_OFFSET}px;
            right: -${TRIM_MARK_BLEED}px;
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }

          /* Top right vertical line (pointing down toward corner) */
          &::after {
            content: '';
            position: absolute;
            top: -${TRIM_MARK_BLEED}px;
            right: ${TRIM_MARK_OFFSET}px;
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }
        `
      case 'bottom-left':
        return css`
          ${baseStyles}

          /* Bottom left horizontal line (pointing right toward corner) */
        &::before {
            content: '';
            position: absolute;
            bottom: ${TRIM_MARK_OFFSET}px;
            left: -${TRIM_MARK_BLEED}px;
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }

          /* Bottom left vertical line (pointing up toward corner) */
          &::after {
            content: '';
            position: absolute;
            bottom: -${TRIM_MARK_BLEED}px;
            left: ${TRIM_MARK_OFFSET}px;
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }
        `
      case 'bottom-right':
        return css`
          ${baseStyles}

          /* Bottom right horizontal line (pointing left toward corner) */
        &::before {
            content: '';
            position: absolute;
            bottom: ${TRIM_MARK_OFFSET}px;
            right: -${TRIM_MARK_BLEED}px;
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }

          /* Bottom right vertical line (pointing up toward corner) */
          &::after {
            content: '';
            position: absolute;
            bottom: -${TRIM_MARK_BLEED}px;
            right: ${TRIM_MARK_OFFSET}px;
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }
        `
      case 'center':
        if (!$showVerticalCenter) return css``
        return css`
          ${baseStyles}

          /* Top center (pointing down toward edge) */
        &::before {
            content: '';
            position: absolute;
            top: -${TRIM_MARK_BLEED}px;
            left: 50%;
            transform: translateX(-50%);
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }

          /* Bottom center (pointing up toward edge) */
          &::after {
            content: '';
            position: absolute;
            bottom: -${TRIM_MARK_BLEED}px;
            left: 50%;
            transform: translateX(-50%);
            width: ${TRIM_MARK_THICKNESS}px;
            height: ${TRIM_MARK_LENGTH}px;
            background: black;
          }
        `
      case 'sides':
        if (!$showHorizontalCenter) return css``
        return css`
          ${baseStyles}

          /* Left center (pointing right toward edge) */
        &::before {
            content: '';
            position: absolute;
            left: -${TRIM_MARK_BLEED}px;
            top: 50%;
            transform: translateY(-50%);
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }

          /* Right center (pointing left toward edge) */
          &::after {
            content: '';
            position: absolute;
            right: -${TRIM_MARK_BLEED}px;
            top: 50%;
            transform: translateY(-50%);
            width: ${TRIM_MARK_LENGTH}px;
            height: ${TRIM_MARK_THICKNESS}px;
            background: black;
          }
        `
      default:
        return css``
    }
  },
)

/** The marks above and below one trifold fold, `$fold` px from the page's
 * left edge, drawn like the middle fold's marks. The element itself sits on
 * the fold, so its `left` is the fold's position on the proof sheet. */
export const FoldMarks = styled.div<{
  $fold: number
  $contentHeight: number
  $productBleedClipped: boolean
}>(({ $fold, $contentHeight, $productBleedClipped }) => {
  if (!$productBleedClipped) return css``
  return css`
    position: absolute;
    top: ${TRIM_MARK_BLEED}px;
    left: ${TRIM_MARK_BLEED + $fold}px;
    width: 0;
    height: ${$contentHeight}px;
    pointer-events: none;

    /* Top of the fold (pointing down toward edge) */
    &::before {
      content: '';
      position: absolute;
      top: -${TRIM_MARK_BLEED}px;
      left: 0;
      transform: translateX(-50%);
      width: ${TRIM_MARK_THICKNESS}px;
      height: ${TRIM_MARK_LENGTH}px;
      background: black;
    }

    /* Bottom of the fold (pointing up toward edge) */
    &::after {
      content: '';
      position: absolute;
      bottom: -${TRIM_MARK_BLEED}px;
      left: 0;
      transform: translateX(-50%);
      width: ${TRIM_MARK_THICKNESS}px;
      height: ${TRIM_MARK_LENGTH}px;
      background: black;
    }
  `
})
